/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { ReactElement, FunctionComponent } from "react";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sidebarAction } from "../reducers/slices/sidebarSlice";
import { IRootState } from "../config/reducers";
import { useAppDispatch } from "../config/store";

export interface BreadcrumbState {
  specific?: string;
  valueReplace?: string;
  indexReplace?: number;
  noTranslate?: boolean;
  textOnly?: boolean;
  replaceLink?: string;
}

type BreadcrumbProps = {
  toggleSecondarySidebar?: () => void;
  state?: BreadcrumbState;
  states?: BreadcrumbState[];
};

export const BreadcrumbRender: FunctionComponent<JSX.Element[]> = (
  children
) => {
  const dispatch = useAppDispatch();
  const { toggleSecondarySidebar } = useSelector(
    (rootState: IRootState) => rootState.sidebarReducer
  );
  const canShowSearchPanelMobile =
    window.location.pathname === "/" ||
    window.location.pathname.indexOf("/Studies") > -1 ||
    window.location.pathname === "/QueryRetrieve" ||
    window.location.pathname === "/RoutingQueue";
  return (
    <div className="page-header page-header-light">
      <div className="breadcrumb-line breadcrumb-line-light header-elements-md-inline">
        <div className="d-flex">
          <div className="breadcrumb">{children}</div>
          {canShowSearchPanelMobile && (
            <a
              aria-hidden
              onClick={() => {
                dispatch(
                  sidebarAction.toggleSecondarySidebar(!toggleSecondarySidebar)
                );
              }}
              className="header-elements-toggle text-default d-md-none"
            >
              <i className="fas fa-filter" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const Breadcrumb = (props: BreadcrumbProps): ReactElement => {
  const { t } = useTranslation();
  const { toggleSecondarySidebar, state, states } = props;
  const location = useLocation();
  const canShowSearchPanelMobile =
    window.location.pathname === "/" ||
    window.location.pathname.indexOf("/Studies") >= -1;
  const paths = location.pathname.split("/");
  let localStates: BreadcrumbState[] = [];
  if (state) {
    localStates.push(state);
  } else if (states) {
    localStates = states;
  }

  const pathRender = paths.map((value: string, index: number) => {
    let nameSpecific;
    let indexSpecific = -1;
    let noTranslation = false;
    let name: string;
    let isReplaceByValue = false;
    const matchedState = localStates.find(
      (s) => s.valueReplace === value || s.indexReplace === index
    );
    if (matchedState) {
      const { specific, indexReplace, noTranslate, valueReplace } =
        matchedState;
      noTranslation = !!noTranslate;
      nameSpecific = specific;
      isReplaceByValue = value === valueReplace;
      indexSpecific = (isReplaceByValue ? index : indexReplace) as number;
    }
    name = value ? t(`breadcrumb.${value.toLowerCase()}`) : value;
    if (index === indexSpecific && nameSpecific) {
      name = noTranslation ? nameSpecific : t(`breadcrumb.${nameSpecific}`);
    }
    // disabled link for Admin , Setting endpoint , specific case router
    if (
      [
        "Admin",
        "Setting",
        "Configuration",
        "Monitoring",
        "Billing",
        "HL7",
      ].includes(value)
    ) {
      return (
        <span key={value} className="breadcrumb-item active">
          {t(`breadcrumb.${value.toLowerCase()}`)}
        </span>
      );
    }
    if (index < paths.length - 1 && !matchedState?.textOnly) {
      const arrPaths = paths.slice(index, index + 1);
      let link = matchedState?.replaceLink;
      if (!link) {
        link =
          arrPaths.filter((x: string) => x === "").length === 1
            ? "/"
            : paths.slice(0, index + 1).join("/");
      }

      return (
        <NavLink
          key={link}
          state={
            indexSpecific !== -1
              ? {
                  specific: nameSpecific,
                  indexReplace: indexSpecific,
                  noTranslate: noTranslation,
                }
              : undefined
          }
          to={link}
          className="breadcrumb-item"
        >
          {index === 0 ? "AdvaPACS Clinician" : name}
        </NavLink>
      );
    }
    return (
      <span key={name} className="breadcrumb-item active">
        {name}
      </span>
    );
  });
  return (
    <div className="page-header page-header-light">
      <div className="breadcrumb-line breadcrumb-line-light header-elements-md-inline">
        <div className="d-flex">
          <div className="breadcrumb">{pathRender}</div>
          {canShowSearchPanelMobile && (
            <a
              aria-hidden
              className="header-elements-toggle text-default d-md-none"
              onClick={() => {
                if (toggleSecondarySidebar) {
                  toggleSecondarySidebar();
                }
              }}
            >
              <i className="fas fa-filter" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default Breadcrumb;
